import { Button, Dropdown, Input } from 'ccs-ui-components';
import { useEffect, useState } from 'react';
import { Col, Form, Row } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { MQ_HEADER_KEY, invalidEventKeys, SERVICE_OPERATION_MAXLENGTH } from '../../../../../../../constants';
import { IHeaderParam } from '../../../../../../../models';
import ServiceOperationHeaderItem from '../param-item/ServiceOpsHeaderItem';

import classes from './MQHeaderParam.module.scss';

interface IMQHeaderParamProps {
  updateHeaderParamList: (params: IHeaderParam[]) => void;
  headerParamListItem: IHeaderParam[];
}
const MQHeaderParam = ({
  updateHeaderParamList,
  headerParamListItem,
}: IMQHeaderParamProps) => {
  const { t: translate } = useTranslation();
  const [headerParamKey, setHeaderParamKey] = useState('');
  const [headerParamKeyIndex, setHeaderParamKeyIndex] = useState<
    number | undefined
  >(undefined);
  const [headerParamValue, setHeaderParamValue] = useState('');
  const [headerParamList, setHeaderParamList] = useState<IHeaderParam[]>(headerParamListItem);
  const [updateHeaderObject, setUpdateHeaderObject] = useState<IHeaderParam>();
  const [headerParamKeyText, setHeaderParamKeyText] = useState('');
  const [isError, setIsError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  const HELPER_TEXT = {
    value_ht: translate('helper_text.txt_value_ht'),
    numeric_ht: translate('helper_text.txt_numeric_ht'),
  };

  useEffect(() => {
    setHeaderParamList(headerParamListItem);
  }, [headerParamListItem]);

  const isKeyExist = () => {
    const key = headerParamKey !== MQ_HEADER_KEY[4].name ? headerParamKey : headerParamKeyText;
    const isKeyExist = headerParamList.some(
      (item) => item.key === key
    );
    if (isKeyExist) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    if (isKeyExist()) {
      setIsError(true);
      setErrorMessage(String(translate('service_operation_details_form.lbl_duplicate_key')));
    } else {
      setIsError(false);
      setErrorMessage('');
    }
  }, [headerParamKey, headerParamKeyText, headerParamList]);

  const [inputType, setInputType] = useState('');
  const typeChangeHandler = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const index = parseInt(e.target.value, 10);
    setHeaderParamKey(MQ_HEADER_KEY[index]?.name);
    setHeaderParamKeyText('');
    setHeaderParamKeyIndex(index);
    setInputType('');
    isKeyExist();
    if (MQ_HEADER_KEY[index]?.name === MQ_HEADER_KEY[3]?.name) {
      setInputType('number');
      setHeaderParamValue('');
    }
  };

  /**
   * @method handleInputChanges
   * @description handles the input values changes for header value
   * @param event Input event
   */
  const handleInputChanges = (event: React.ChangeEvent<HTMLInputElement>) => {
    if ((inputType === 'number' && parseInt(event.target.value, 10) < 0) || (inputType === 'number' && event.target.value?.includes('.'))) {
      return;
    }
    setHeaderParamValue(event.target.value);
  };

  const handleKeyInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setHeaderParamKeyText(event.target.value);
    if (event.target.value === MQ_HEADER_KEY[3]?.name) {
      setInputType('number');
    } else {
      setInputType('');
    }
  };

  /**
   * @method onAddHeaderHandler
   * @description call when Add header button clicked
   */
  const onAddHeaderHandler = () => {
    const tempHeader = [...headerParamList];
    const tempHeaderObj = {
      key: headerParamKey !== MQ_HEADER_KEY[4].name ? headerParamKey : headerParamKeyText,
      value: headerParamValue,
      isEdit: false
    };
    tempHeader.push(tempHeaderObj);
    setHeaderParamList(tempHeader);
    setHeaderParamKey('');
    setHeaderParamValue('');
    setHeaderParamKeyIndex(-1);
    setHeaderParamKeyText('');
    setInputType('');
  };

  /**
   * @method onHeaderItemChange
   * @description calls when header update button clicked
   * @param obj header item to be update
   */
  const onHeaderItemChange = (obj: IHeaderParam) => {
    setUpdateHeaderObject(obj);
  };

  /**
   * @method onHeaderItemDelete
   * @description call on delete header item button clicked
   * @param index delete header item index
   */
  const onHeaderItemDelete = (index: number) => {
    const headerData = [...headerParamList];
    headerData.splice(index, 1);
    setHeaderParamList(headerData);
  };

  /**
   * @method validateAddHeaderButton
   * @description Validates the Add button
   * @returns disable true else false
   */
  const validateAddHeaderButton = () => {
    if (
      headerParamKey === MQ_HEADER_KEY[4].name
      && (headerParamKeyText.length <= 0 || headerParamValue.length <= 0)
    ) {
      return true;
    }
    if (headerParamKey.length <= 0 || headerParamValue.length <= 0) {
      return true;
    }
    if ((isKeyExist())) {
      return true;
    }
    return false;
  };

  useEffect(() => {
    const tempIndex = updateHeaderObject?.index ? updateHeaderObject.index : 0;
    if (updateHeaderObject) {
      const tempHeaderParamList = [...headerParamList];
      tempHeaderParamList.splice(tempIndex, 1, updateHeaderObject);
      setHeaderParamList(tempHeaderParamList);
    }
  }, [updateHeaderObject]);

  useEffect(() => {
    updateHeaderParamList(headerParamList);
  }, [headerParamList]);

  const getClassNameForKey = () => {
    if (headerParamKey === MQ_HEADER_KEY[4].name) {
      return 'dropdown-control-mq';
    }
    if (headerParamKey === MQ_HEADER_KEY[3].name || headerParamKeyText === MQ_HEADER_KEY[3].name) {
      return 'dropdown-control-time';
    }
    return '';
  };

  const getClassNameForAddButton = () => {
    if (headerParamKey === MQ_HEADER_KEY[3].name || headerParamKeyText === MQ_HEADER_KEY[3].name) {
      return 'add-button-time';
    }
    return '';
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    const { key } = event;
    if (inputType === 'number' && invalidEventKeys.includes(key.toLowerCase())) {
      event.preventDefault();
    }
  };

  return (
    <div>
      <Row className={classes['param-body']}>
        <Col sm={5}>
          <Form.Group controlId="headerkey">
            <Dropdown
              label=""
              onChange={typeChangeHandler}
              value={headerParamKeyIndex}
              data={MQ_HEADER_KEY}
              name="headerKey"
              data-testid="operation-verb"
              className={`${classes['dropdown-control']} ${classes[getClassNameForKey()]}`}
              error={headerParamKey !== MQ_HEADER_KEY[4].name && isError}
              errorTxt={headerParamKey !== MQ_HEADER_KEY[4].name ? errorMessage : ''}
              placeholder={String(translate('fsm_registry_form.lbl_placeholder_select'))}
            />
          </Form.Group>
        </Col>
        {headerParamKey !== MQ_HEADER_KEY[4].name && (
          <Col sm={5}>
            <Form.Group controlId="headerValue">
              <Input
                label=""
                placeholder={
                  inputType.length
                    ? String(translate('service_operation_form.header_param_value_num'))
                    : String(translate('service_operation_form.header_param_value'))
                }
                onChange={handleInputChanges}
                value={headerParamValue}
                helperText={!inputType.length ? HELPER_TEXT.value_ht : HELPER_TEXT.numeric_ht}
                name="headerValue"
                data-testid="header-value"
                type={inputType}
                onKeyUp={onKeyDown}
                onKeyDown={onKeyDown}
              />
            </Form.Group>
          </Col>
        )}
        {headerParamKey !== MQ_HEADER_KEY[4].name && (
          <Col sm={2}>
            <Button
              className={`${classes['add-button']} ${classes[getClassNameForAddButton()]}`}
              data-testid="add-header"
              onClick={() => onAddHeaderHandler()}
              variant="secondary"
              disabled={validateAddHeaderButton()}
            >
              {translate('service_operation_form.add_button')}
            </Button>
          </Col>
        )}
      </Row>
      {headerParamKey === MQ_HEADER_KEY[4].name && (
        <Row className={classes['param-body']}>
          <Col sm={5}>
            <Form.Group controlId="headerkey">
              <Input
                name="headerParamValue"
                label=""
                onChange={handleKeyInputChange}
                value={headerParamKeyText}
                data-testid="header-key"
                placeholder={
                  String(translate('service_operation_form.header_param_value'))
                }
                maxLength={Number(SERVICE_OPERATION_MAXLENGTH.headerParamMaxLength)}
                helperText={HELPER_TEXT.value_ht}
                id="headerParamValue"
                error={isError}
                errorTxt={errorMessage}
              />
            </Form.Group>
          </Col>
          <Col sm={5} className={headerParamKeyText === MQ_HEADER_KEY[3].name ? classes['input-spinner-time'] : ''}>
            <Form.Group controlId="headerValue">
              <Input
                label=""
                placeholder={
                  inputType.length
                    ? String(translate('service_operation_form.header_param_value_num'))
                    : String(translate('service_operation_form.header_param_value'))
                }
                onChange={handleInputChanges}
                value={headerParamValue}
                helperText={!inputType.length ? HELPER_TEXT.value_ht : HELPER_TEXT.numeric_ht}
                name="headerValue"
                data-testid="header-value"
                type={inputType}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyDown}
              />
            </Form.Group>
          </Col>
          <Col sm={2}>
            <Button
              className={classes['add-button']}
              data-testid="add-header"
              onClick={() => onAddHeaderHandler()}
              variant="secondary"
              disabled={validateAddHeaderButton()}
            >
              {translate('service_operation_form.add_button')}
            </Button>
          </Col>
        </Row>
      )}
      <Row>
        {headerParamList?.length >= 1
          && headerParamList.map((headerItem, index) => (
            <ServiceOperationHeaderItem
              index={index}
              key={headerItem.key}
              headerParamKey={headerItem.key}
              value={headerItem.value}
              isEdit={headerItem.isEdit}
              isView={false}
              updateHeader={onHeaderItemChange}
              deleteHeader={onHeaderItemDelete}
              headerList={headerParamList}
            />
          ))}
      </Row>
    </div>
  );
};
export default MQHeaderParam;
